/**
 * decree-procedures/error-keys — mapowanie kodów błędów backendu (walidacja strukturalna SQL +
 * egzekutor procedur dekretacji) na klucze i18n czytelnych komunikatów.
 *
 * Współdzielony przez `DecreeProceduresTab.tsx` — sekcja „Testuj" w modalu formularza
 * (`POST /decree-procedures/test-sql`) ORAZ create/update (`POST`/`PATCH .../decree-procedures`)
 * mogą zwrócić te same kody `SQL_<code>` (400), bo obie trasy wołają ten sam walidator
 * strukturalny PRZED zapisem/wykonaniem — mirror kontraktu backendu,
 * `apps/api/lib/dictionaries/sql-source-validator.ts` + `apps/api/lib/documents/decree-procedure-executor.ts`.
 *
 * Kody specyficzne dla dekretacji (nieobecne w `record-procedures/error-keys.ts`):
 *  - `DECREE_PROCEDURE_ROW_CAP` — procedura zwróciła za dużo wierszy (propozycja dekretu ma z
 *    natury być mała).
 *  - `UNSUPPORTED_DOCUMENT_CLASS` / `DOCUMENT_MISSING_COMPANY` / `DOCUMENT_NOT_FOUND` — trasa
 *    `test-sql` waliduje dokument PRZED wykonaniem SQL (autor testuje na realnej fakturze).
 *  - `PROCEDURE_NOT_FOUND` / `PROCEDURE_INACTIVE` — używane przez `POST .../run` (belka
 *    dekretacji, `DecreeProcedureRunControls.tsx`).
 *
 * `DECREE_SUGGEST_ACCOUNT_INVALID` NIE jest już zwracane przez `run`/`test-sql` — konto na
 * wierszu dekretu to wolny tekst, bez walidacji względem `chart_of_accounts` (uproszczenie
 * modelu dekretacji). Kod zostaje wyłącznie w `decree-templates/suggest` („Podpowiedz dekret",
 * osobny, świadomie nietknięty mechanizm) — patrz `apps/admin/src/components/documents/
 * DecreeSuggestControls.tsx`, które ma WŁASNE mapowanie tego kodu, nie to.
 *
 * `EXECUTION_FAILED` (422, `.../test-sql` i `.../run`) NIE niesie już surowego komunikatu
 * Postgresa w odpowiedzi (przeciek danych) — tylko `sqlState`. Klucz zwraca tekst generyczny;
 * caller dokłada `sqlState` do wyświetlanego komunikatu samodzielnie, jeśli obecny.
 */

/**
 * Klucz zwracany dla kodu bez dedykowanego mapowania (w tym `undefined`/`VALIDATION_ERROR`).
 * Eksportowany, żeby callerzy poza modalem „Testuj" (np. `DecreeProcedureRunControls.tsx`) mogli
 * odróżnić „mam konkretny komunikat" od „nic nie pasowało" i użyć WŁASNEGO tekstu generycznego
 * dopasowanego do kontekstu (uruchomienie procedury ≠ test SQL w formularzu).
 */
export const DECREE_PROCEDURE_SQL_ERROR_GENERIC_KEY = 'decreeProcedures.form.sqlErrors.generic';

export function decreeProcedureSqlErrorKey(code: string | undefined): string {
  switch (code) {
    case 'SQL_NOT_SELECT':
      return 'decreeProcedures.form.sqlErrors.notSelect';
    case 'SQL_MULTIPLE_STATEMENTS':
      return 'decreeProcedures.form.sqlErrors.multipleStatements';
    case 'SQL_PARAM_UNDECLARED':
      return 'decreeProcedures.form.sqlErrors.paramUndeclared';
    case 'SQL_EMPTY':
      return 'decreeProcedures.form.sqlErrors.empty';
    case 'SQL_FORBIDDEN_KEYWORD':
      return 'decreeProcedures.form.sqlErrors.forbiddenKeyword';
    case 'SQL_UNSUPPORTED_SYNTAX':
      return 'decreeProcedures.form.sqlErrors.unsupportedSyntax';
    case 'EXTERNAL_PARAM_MISSING':
      return 'decreeProcedures.form.sqlErrors.paramMissing';
    case 'EXTERNAL_PARAM_INVALID':
      return 'decreeProcedures.form.sqlErrors.paramInvalid';
    case 'DECREE_PROCEDURE_RESULT_SHAPE':
      return 'decreeProcedures.form.sqlErrors.resultShape';
    case 'DECREE_PROCEDURE_ROW_CAP':
      return 'decreeProcedures.form.sqlErrors.rowCap';
    case 'UNSUPPORTED_DOCUMENT_CLASS':
      return 'decreeProcedures.form.sqlErrors.unsupportedDocumentClass';
    case 'DOCUMENT_MISSING_COMPANY':
      return 'decreeProcedures.form.sqlErrors.documentMissingCompany';
    case 'DOCUMENT_NOT_FOUND':
      return 'decreeProcedures.form.sqlErrors.documentNotFound';
    case 'EXECUTION_FAILED':
      return 'decreeProcedures.form.sqlErrors.executionFailed';
    case 'PROCEDURE_NOT_FOUND':
      return 'decreeProcedures.form.sqlErrors.procedureNotFound';
    case 'PROCEDURE_INACTIVE':
      return 'decreeProcedures.form.sqlErrors.procedureInactive';
    default:
      // Obejmuje też `VALIDATION_ERROR` (400 Zod, rzadki przypadek — walidacja klienta pokrywa
      // pola po stronie UI) — bez dedykowanego klucza.
      return DECREE_PROCEDURE_SQL_ERROR_GENERIC_KEY;
  }
}
